import { createBrowserRouter } from "react-router";
import { RequireAdmin } from "./components/RequireAdmin";
import { AccountSettingsPage } from "./pages/AccountSettingsPage";
import { AiModelFormPage } from "./pages/AiModelFormPage";
import { AiModelsPage } from "./pages/AiModelsPage";
import { AiUsagePage } from "./pages/AiUsagePage";
import { AnnouncementFormPage } from "./pages/AnnouncementFormPage";
import { AnnouncementsPage } from "./pages/AnnouncementsPage";
import { AppDetailPage } from "./pages/AppDetailPage";
import { AppsPage } from "./pages/AppsPage";
import { AuditPage } from "./pages/AuditPage";
import { DatabasesPage } from "./pages/DatabasesPage";
import { DeploymentsPage } from "./pages/DeploymentsPage";
import { EmailDomainsPage } from "./pages/EmailDomainsPage";
import { CasesPage } from "./pages/safety/CasesPage";
import { CaseDetailPage } from "./pages/safety/CaseDetailPage";
import { MonitorPage } from "./pages/safety/MonitorPage";
import { EmailLogPage } from "./pages/EmailLogPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OrgDetailPage } from "./pages/OrgDetailPage";
import { OrgsPage } from "./pages/OrgsPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PlansPage } from "./pages/PlansPage";
import { SchedulesPage } from "./pages/SchedulesPage";
import { TunnelsPage } from "./pages/TunnelsPage";
import { DomainsPage } from "./pages/domains/DomainsPage";
import { DomainsTab } from "./pages/domains/DomainsTab";
import { CatalogueTab } from "./pages/domains/CatalogueTab";
import { OrdersTab } from "./pages/domains/OrdersTab";
import { AddonsPage } from "./pages/AddonsPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { RevenuePage } from "./pages/RevenuePage";
import { PromotionsPage } from "./pages/PromotionsPage";
import { CurrenciesPage } from "./pages/CurrenciesPage";
import { InferenceCampaignsPage } from "./pages/InferenceCampaignsPage";
import { QuotaRequestsPage } from "./pages/QuotaRequestsPage";
import {
  ServerDetailPage,
  ServerDiagnosticsTab,
  ServerDiskTab,
  ServerOverviewTab,
  ServerSettingsTab,
  ServerTenantsTab,
} from "./pages/ServerDetailPage";
import { ServerCreatePage } from "./pages/ServerCreatePage";
import { ServersPage } from "./pages/ServersPage";
import { SupportPage } from "./pages/SupportPage";
import { TicketDetailPage } from "./pages/support/TicketDetailPage";
import { UserDetailPage } from "./pages/UserDetailPage";
import { UsersPage } from "./pages/UsersPage";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  {
    element: <RequireAdmin />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "servers", element: <ServersPage /> },
      /* Static beats dynamic in the ranker, so this cannot be shadowed by
         `servers/:serverId` — but it is written first so a reader does not have
         to know that to be sure. */
      { path: "servers/new", element: <ServerCreatePage /> },
      // Tabs are real routes so an operator can paste one into an incident
      // channel and land a colleague on the same panel.
      {
        path: "servers/:serverId",
        element: <ServerDetailPage />,
        children: [
          { index: true, element: <ServerOverviewTab /> },
          { path: "diagnostics", element: <ServerDiagnosticsTab /> },
          { path: "disk", element: <ServerDiskTab /> },
          { path: "tenants", element: <ServerTenantsTab /> },
          { path: "settings", element: <ServerSettingsTab /> },
        ],
      },
      { path: "revenue", element: <RevenuePage /> },
      { path: "currencies", element: <CurrenciesPage /> },
      { path: "promotions", element: <PromotionsPage /> },
      { path: "free-inference", element: <InferenceCampaignsPage /> },
      { path: "plans", element: <PlansPage /> },
      { path: "schedules", element: <SchedulesPage /> },
      { path: "tunnels", element: <TunnelsPage /> },
      {
        path: "domains",
        element: <DomainsPage />,
        children: [
          { index: true, element: <DomainsTab /> },
          { path: "catalogue", element: <CatalogueTab /> },
          { path: "orders", element: <OrdersTab /> },
        ],
      },
      { path: "addons", element: <AddonsPage /> },
      { path: "ai-models", element: <AiModelsPage /> },
      { path: "ai-usage", element: <AiUsagePage /> },
      { path: "ai-models/new", element: <AiModelFormPage /> },
      /* A splat, because a public id carries a slash and is two path segments.
         Percent-encoding it into one is a fight with the browser, the router and
         any proxy in between; the API took the same route with `:modelId{.+}`. */
      { path: "ai-models/edit/*", element: <AiModelFormPage /> },
      { path: "announcements", element: <AnnouncementsPage /> },
      /* Static before dynamic, so `new` cannot be read as an id. The ranker
         would get this right anyway; written in this order so a reader does not
         have to know that to be sure. */
      { path: "announcements/new", element: <AnnouncementFormPage /> },
      { path: "announcements/:announcementId", element: <AnnouncementFormPage /> },
      /* The queue, and one ticket. Static before dynamic, like everything else
         here — the ranker would get it right anyway, and the order says so
         without the reader having to know that. */
      { path: "support", element: <SupportPage /> },
      { path: "support/:ticketId", element: <TicketDetailPage /> },
      { path: "organizations", element: <OrgsPage /> },
      /* Before ":orgId", so the queue is never read as an organization. */
      { path: "organizations/limit-requests", element: <QuotaRequestsPage /> },
      { path: "organizations/:orgId", element: <OrgDetailPage /> },
      { path: "users", element: <UsersPage /> },
      { path: "users/:userId", element: <UserDetailPage /> },
      { path: "email-domains", element: <EmailDomainsPage /> },
      { path: "safety", element: <CasesPage /> },
      /* Before ":caseId", like every static-before-dynamic pair here. */
      { path: "safety/monitor", element: <MonitorPage /> },
      { path: "safety/:caseId", element: <CaseDetailPage /> },
      { path: "apps", element: <AppsPage /> },
      { path: "apps/:appId", element: <AppDetailPage /> },
      { path: "databases", element: <DatabasesPage /> },
      { path: "deployments", element: <DeploymentsPage /> },
      { path: "emails", element: <EmailLogPage /> },
      { path: "audit", element: <AuditPage /> },
      { path: "security", element: <AccountSettingsPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
